/**
 * The Specification stage's Detailed Description framework as one query, and its writes.
 *
 * Every write answers the framework whole with what is derived from it, so the cache takes
 * the answer as it is. Writes go one at a time: each edit is made against the framework the
 * last write answered, under its version, so two quick edits never race each other. A
 * refusal because another window saved first reloads the framework and says so; any other
 * refusal is given in the server's words.
 */

import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  DetailedDescriptionFramework,
  FrameworkResponse,
  SectionEdit,
} from "@patent-studio/api-types";
import { ApiError, refusalDetail } from "@/services/api-client";
import {
  confirmFramework,
  getFramework,
  proposeFramework,
  replaceFramework,
} from "@/services/specification";
import { MUTATION_KEYS } from "@/lib/analytics/mutation-reporting";
import { ddFrameworkKey } from "./query-keys";
import { takeServed } from "./take-served";

export const CHANGED_ELSEWHERE =
  "This framework changed in another window. It has been reloaded.";
const NOT_SAVED = "The framework could not be saved. Try again.";

export function useDdFramework(taskId: string) {
  return useQuery({
    queryKey: ddFrameworkKey(taskId),
    queryFn: () => getFramework(taskId),
  });
}

/** The code the server gives a write built on a version another write has replaced. */
const VERSION_CONFLICT = "FRAMEWORK_VERSION_CONFLICT";

/** An edit the page refuses itself, because it cannot be made against what the server holds. */
class EditRefused extends Error {}

/**
 * An edit as a function of the framework the server holds when the edit is sent, so it is
 * made against the last write's answer. Null when it cannot be made there.
 */
export type FrameworkEdit = (
  served: DetailedDescriptionFramework,
) => SectionEdit[] | null;

/** One queue per task: a write waits for the one before it to land. */
function writes(taskId: string) {
  return { id: `dd-framework:${taskId}` };
}

function holding(qc: QueryClient, taskId: string): FrameworkResponse {
  const served = qc.getQueryData<FrameworkResponse>(ddFrameworkKey(taskId));
  if (served === undefined) throw new EditRefused(NOT_SAVED);
  return served;
}

/**
 * Tell the attorney a write did not land. A refusal the page made itself cannot
 * go again unchanged; any other can, because each write replaces the framework
 * whole under the version it read, so a second send of one that did land after
 * all is refused as stale and reloads rather than applying twice.
 */
function refused(
  qc: QueryClient,
  taskId: string,
  error: Error,
  retry: () => void,
): void {
  if (error instanceof EditRefused) {
    toast.error(error.message);
    return;
  }
  if (error instanceof ApiError && error.code === VERSION_CONFLICT) {
    toast.info(CHANGED_ELSEWHERE);
    void qc.invalidateQueries({ queryKey: ddFrameworkKey(taskId) });
    return;
  }
  toast.error(refusalDetail(error, NOT_SAVED), {
    action: { label: "Retry", onClick: retry },
  });
}

/** Replace the framework by an edit made against what the server last answered. */
export function useReplaceFramework(taskId: string) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationKey: MUTATION_KEYS.frameworkReplace,
    scope: writes(taskId),
    mutationFn: ({
      edit,
      refused: refusal = NOT_SAVED,
    }: {
      edit: FrameworkEdit;
      /** What the attorney is told when the edit cannot be made. */
      refused?: string;
    }) => {
      const { framework } = holding(qc, taskId);
      const sections = edit(framework);
      if (sections === null) throw new EditRefused(refusal);
      return replaceFramework(taskId, {
        sections,
        version: framework.version,
      });
    },
    onSuccess: (served) => takeServed(qc, ddFrameworkKey(taskId), served),
    onError: (error, asked) => {
      refused(qc, taskId, error, () => {
        mutation.mutate(asked);
      });
    },
  });
  return mutation;
}

/** Confirm the framework as the last write left it, by the address it was served. */
export function useConfirmFramework(taskId: string) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationKey: MUTATION_KEYS.frameworkConfirm,
    scope: writes(taskId),
    mutationFn: () => confirmFramework(taskId, holding(qc, taskId).address),
    onSuccess: (served) => takeServed(qc, ddFrameworkKey(taskId), served),
    onError: (error) => {
      refused(qc, taskId, error, () => {
        mutation.mutate();
      });
    },
  });
  return mutation;
}

/** Ask Praxis for a framework; the run lands through the worker, and the stream says when. */
export function useProposeFramework(
  taskId: string,
  failed = "The framework could not be proposed.",
) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.frameworkPropose,
    mutationFn: () => proposeFramework(taskId),
    onSuccess: (served) => takeServed(qc, ddFrameworkKey(taskId), served),
    onError: (error) => {
      toast.error(refusalDetail(error, failed));
    },
  });
}
